import { localeMeta } from "@/lib/seo-head";
import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";

import { RequestForm } from "@/components/site/RequestForm";
import { Section } from "@/components/site/Section";
import { alternateLinks, normalizeLocale } from "@/lib/i18n";
import { useT } from "@/lib/i18n-dict";
import { getAddons } from "@/lib/pricing.functions";

const TITLE = "Book a live demo | factory.online";
const DESCRIPTION =
  "See factory.online walked through on your production scenario: planning, scheduling, inventory, purchasing and cost in one live session.";

const PERKS = [
  "45 minutes, tailored to your production model",
  "Walkthrough of planning, scheduling and costing",
  "Answers on integrations and data migration",
  "A clear rollout plan and pricing estimate",
];

export const Route = createFileRoute("/{-$locale}/registration-demo")({
  staticData: { sitemap: true },
  /** `?addon=<slug>` pre-fills the request with the add-on the visitor came from. */
  validateSearch: (search: Record<string, unknown>) => ({
    addon: typeof search["addon"] === "string" ? (search["addon"] as string) : undefined,
  }),
  loaderDeps: ({ search }) => ({ addon: search.addon }),
  loader: async ({ deps, params }) => {
    if (!deps.addon) return { addonName: "" };
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const addons = await getAddons({ data: { locale } });
    return { addonName: addons.find((a) => a.slug === deps.addon)?.name ?? "" };
  },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/registration-demo"),
  });
  },
  component: DemoPage,
});

function DemoPage() {
  const { addonName } = Route.useLoaderData();
  const t = useT();
  const defaultMessage = addonName
    ? t("I'd like to see the {addon} add-on in action and understand how it fits our production setup.").replace("{addon}", addonName)
    : "";

  return (
    <Section className="hero-glow">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.05fr]">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
            {addonName ? `${t("Live demo")} — ${addonName}` : t("Live demo")}
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold text-balance-tight md:text-5xl">
            {t("See factory.online on your own production scenario")}
          </h1>
          <p className="mt-5 text-lg text-muted-foreground">
            {t("A consultant walks you through the modules that matter for your plant and answers the hard questions about data, integrations and rollout.")}
          </p>
          <ul className="mt-8 grid gap-3">
            {PERKS.map((perk) => (
              <li key={perk} className="flex gap-3 text-sm text-muted-foreground">
                <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                {t(perk)}
              </li>
            ))}
          </ul>
        </div>
        <RequestForm
          kind="demo"
          withMessage
          defaultMessage={defaultMessage}
          submitLabel="Book the demo"
          successTitle="Demo request received"
          successText="We'll reach out shortly to agree on a time that suits your team."
        />
      </div>
    </Section>
  );
}
